import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { Linking, Platform, StatusBar, StyleSheet, View } from 'react-native';
import { WebView } from 'utils/packages/webview';
import { setPendingDeeplinkAction } from 'actions';
import { DeepLinkType, getOneLinkDeepLinkValue, parseDeepLink, } from 'common/NavigatorActions/hooks/parseDeepLink';
import { NavigationTextButton } from 'design-system';
import { useHeaderHeight } from 'hooks';
import { isValidAppLink } from 'hooks/useAppLink';
import { selectPasscodeVisible, selectUser } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { DEFAULT_DELAY_TIME } from 'utils/delay';
import { selectUserTier } from 'features/premium/selectors/tier';
import Modal from 'utils/packages/Modal';
import getSupportUrl from '../utils/getSupportUrl';
const BLUR_KEYBOARD = `
  document.activeElement.blur();
`;
const EMMA_APP_WEB_URL = /^https:\/\/(www\.)?emma-app\.com\/dashboard\//;
const isHandledInApp = (url) => {
    const deepLinkData = parseDeepLink(url);
    if (deepLinkData.type === DeepLinkType.NONE) {
        return false;
    }
    return (deepLinkData.type !== DeepLinkType.APP_LINK ||
        isValidAppLink(deepLinkData.routeStr));
};
/**
 * Returns the deeplink to handle in the app, or undefined when the link should
 * open externally. Only `/dashboard/` paths on emma-app.com count as app links,
 * so website pages that share a path with a screen still open in the browser.
 */
const getInAppDeepLink = (url) => {
    const oneLinkValue = getOneLinkDeepLinkValue(url);
    if (oneLinkValue) {
        return isHandledInApp(oneLinkValue) ? oneLinkValue : undefined;
    }
    if (!url.startsWith('emma://') && !EMMA_APP_WEB_URL.test(url)) {
        return undefined;
    }
    return isHandledInApp(url) ? url : undefined;
};
const SupportModal = ({ navigation, route }) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const userTier = useAppSelector(selectUserTier);
    const isLoggedIn = useAppSelector((store) => store.user.isLoggedIn);
    const isPasscodeVisible = useAppSelector(selectPasscodeVisible);
    const headerHeight = useHeaderHeight();
    const webViewRef = useRef(null);
    useEffect(() => {
        if (Platform.OS === 'android') {
            webViewRef.current?.requestFocus();
        }
    }, []);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'prioritySupport',
    });
    useLayoutEffect(() => {
        const { isWaitingScreen } = route.params ?? {};
        const active = isUnlockedForSpace;
        let timeout;
        const openPriority = () => {
            webViewRef.current?.injectJavaScript(BLUR_KEYBOARD);
            timeout = setTimeout(() => {
                onPressUpgrade();
            }, DEFAULT_DELAY_TIME);
            Amplitude.logEvent('Support.openPremium');
        };
        const hasPriority = !(!active &&
            isLoggedIn &&
            !isWaitingScreen &&
            !isPasscodeVisible);
        navigation.setOptions({
            title: 'Support',
            headerTransparent: true,
            headerRight: () => hasPriority === false ? (<NavigationTextButton text="Priority" onPress={openPriority} color={{ White: true }}/>) : null,
        });
        return () => {
            clearTimeout(timeout);
        };
    }, [
        isLoggedIn,
        isPasscodeVisible,
        isUnlockedForSpace,
        onPressUpgrade,
        route.params,
        user,
    ]);
    const email = user ? user.email : null;
    const { nickName, url } = getSupportUrl(user);
    const object = { uri: url };
    const [loadFinished, setLoadFinished] = useState(false);
    useEffect(() => {
        if (loadFinished) {
            const timeout = setTimeout(() => {
                webViewRef.current?.injectJavaScript(`
      if ($crisp) {
        $crisp.push(["set", "user:nickname", ['${nickName}']]);
      }

      true`);
            }, 1000);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
    }, [nickName, loadFinished]);
    useEffect(() => {
        if (loadFinished && user.vulnerableCustomerStatus) {
            const timeout = setTimeout(() => {
                webViewRef.current?.injectJavaScript(`
          $crisp.push(["set", "session:data", [[
            ["vulnerable_customer_status", "${user.vulnerableCustomerStatus}"],
          ]]]);

      true`);
            }, 1000);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
    }, [loadFinished, user.vulnerableCustomerStatus]);
    useEffect(() => {
        if (loadFinished) {
            const timeout = setTimeout(() => {
                webViewRef.current?.injectJavaScript(`
          $crisp.push(["set", "session:data", [[
            ["tier", "${userTier}"],
          ]]]);

      true`);
            }, 1000);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
    }, [loadFinished, userTier]);
    useEffect(() => {
        if (loadFinished) {
            const timeout = setTimeout(() => {
                webViewRef.current?.injectJavaScript(`
      if ($crisp) {
        $crisp.push(["set", "user:email", ['${email}' || '']]);
      }

      true`);
            }, 1000);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
    }, [email, loadFinished]);
    const openLink = useCallback((linkUrl) => {
        const deepLink = getInAppDeepLink(linkUrl);
        if (deepLink) {
            if (!isLoggedIn) {
                Modal.showError('Sign in to your Emma account to open this link', undefined, 'Hold on 💫');
                return;
            }
            dispatch(setPendingDeeplinkAction(deepLink));
            return;
        }
        Linking.canOpenURL(linkUrl).then((supported) => {
            if (supported) {
                Linking.openURL(linkUrl).catch(() => { });
            }
        });
    }, [isLoggedIn]);
    const onOpenWindow = useCallback((event) => {
        openLink(event.nativeEvent.targetUrl);
    }, [openLink]);
    return (<View style={styles.wrap}>
      <StatusBar barStyle="light-content" animated/>
      <View style={[styles.header, { height: headerHeight }]}/>
      <WebView ref={webViewRef} bounces={false} autoManageStatusBarEnabled={false} source={object} originWhitelist={['*']} scalesPageToFit javaScriptEnabled startInLoadingState hideKeyboardAccessoryView automaticallyAdjustContentInsets thirdPartyCookiesEnabled={false} onOpenWindow={onOpenWindow} onShouldStartLoadWithRequest={(event) => {
            if (event.url !== url && event.url !== 'about:blank') {
                openLink(event.url);
                return false;
            }
            return true;
        }} onLoadEnd={() => {
            setLoadFinished(true);
        }}/>
    </View>);
};
const styles = StyleSheet.create({
    wrap: {
        flex: 1,
    },
    header: {
        backgroundColor: 'rgb(127,44,255)',
    },
});
export default SupportModal;
